import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import './PrivacyPage.css';

export default function TermsPage() {
  usePageMeta(
    'Terms of Use',
    'The rules and conditions for using The Gardeners Calendar.',
  );
  return (
    <div className="privacy-page">
      <div className="privacy-header">
        <div className="container-narrow">
          <p className="text-label" style={{ color: 'var(--sage)', marginBottom: 'var(--space-3)' }}>Legal</p>
          <h1 className="text-display">Terms of Use</h1>
          <p className="text-subtitle" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-3)' }}>
            The rules and conditions for using The Gardeners Calendar.
          </p>
          <p className="text-caption" style={{ color: 'var(--ink-faint)', marginTop: 'var(--space-4)' }}>
            Last updated: March 2026
          </p>
        </div>
      </div>

      <div className="container-narrow privacy-body">
        <article className="paper prose privacy-article">

          <section>
            <h2>About These Terms</h2>
            <p>
              These Terms of Use govern your access to and use of The Gardeners Calendar website at <strong>the-gardeners-calendar.co.uk</strong>, operated by White Pebble Projects. By using this site you agree to these terms. If you do not agree, please do not use the site.
            </p>
            <p>
              These terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2>The Service</h2>
            <p>
              The Gardeners Calendar provides free access to gardening information, a biodynamic planting calendar, a plant database, a growers directory, and — for registered users — a personal garden journal and diary.
            </p>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the service at any time without notice. We will not be liable to you or any third party for any such change, suspension, or discontinuation.
            </p>
          </section>

          <section>
            <h2>Your Account</h2>
            <p>
              To use the My Garden and Dashboard features, you must sign in via an approved authentication provider (Google, Facebook, GitHub, or Microsoft). By signing in, you represent that:
            </p>
            <ul>
              <li>You are at least 13 years of age</li>
              <li>You are authorised to use the authentication account you sign in with</li>
              <li>The information associated with your account is accurate</li>
            </ul>
            <p>
              You are responsible for maintaining the security of your account and for all activity that occurs under it. If you suspect unauthorised access to your account, please contact us immediately.
            </p>
            <p>
              We reserve the right to terminate or suspend accounts that violate these terms, at our sole discretion.
            </p>
          </section>

          <section>
            <h2>Acceptable Use</h2>
            <p>You agree not to use the site to:</p>
            <ul>
              <li>Post, upload, or share content that is unlawful, harmful, threatening, abusive, defamatory, or otherwise objectionable</li>
              <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
              <li>Attempt to gain unauthorised access to any part of the site or its underlying infrastructure</li>
              <li>Interfere with or disrupt the integrity or performance of the service</li>
              <li>Scrape, crawl, or systematically extract data from the site without our written permission</li>
              <li>Use the site for any commercial purpose without our express written consent</li>
              <li>Circumvent any technical measures we use to provide or protect the service</li>
            </ul>
          </section>

          <section>
            <h2>Your Content</h2>
            <p>
              Content you enter into your garden journal — including plant records, diary entries, and garden details — remains yours. You grant us a limited licence to store and display that content solely for the purpose of providing the service to you.
            </p>
            <p>
              You are responsible for any content you submit. We do not claim ownership of your personal garden data, and we will not share it with third parties except as described in our <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2>Our Content</h2>
            <p>
              All other content on this site — including the plant database, growing guides, biodynamic calendar, articles, illustrations, and design — is owned by or licensed to White Pebble Projects. It is protected by copyright and other intellectual property laws.
            </p>
            <p>
              You may browse and use this content for your own personal, non-commercial gardening purposes. You may not reproduce, republish, distribute, or create derivative works from our content without our written permission.
            </p>
          </section>

          <section>
            <h2>Growers Directory</h2>
            <p>
              The Growers Directory contains listings for nurseries, garden designers, suppliers, bloggers, and other businesses. These listings are provided for informational purposes only.
            </p>
            <p>
              We do not endorse, verify, or take responsibility for any business, service, or content listed in the directory. Any dealings you have with listed businesses are between you and them. We are not liable for any loss or damage arising from such dealings.
            </p>
            <p>
              If you submit a listing or review, you confirm that the information is accurate and that you have the right to submit it. We reserve the right to remove any listing or review at our discretion.
            </p>
          </section>

          <section>
            <h2>Gardening Advice</h2>
            <p>
              The growing guides, biodynamic information, and gardening advice on this site are provided for general informational purposes only. While we aim for accuracy, gardening outcomes depend on many factors including soil conditions, climate, local weather, and individual skill.
            </p>
            <p>
              We make no guarantee that following any advice on this site will produce particular results. Always use your own judgement and, where appropriate, consult a qualified horticultural professional.
            </p>
          </section>

          <section>
            <h2>Advertising</h2>
            <p>
              This site displays advertisements served by Google AdSense. These ads help us keep the service free. Advertisements are clearly distinguished from editorial content.
            </p>
            <p>
              We do not endorse any products or services advertised on this site. The appearance of an advertisement does not constitute our recommendation of the advertiser or their products.
            </p>
          </section>

          <section>
            <h2>Disclaimer of Warranties</h2>
            <p>
              The Gardeners Calendar is provided on an "as is" and "as available" basis, without warranties of any kind, either express or implied. We do not warrant that the service will be uninterrupted, error-free, or free of viruses or other harmful components.
            </p>
            <p>
              To the fullest extent permitted by law, we disclaim all warranties including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
          </section>

          <section>
            <h2>Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, White Pebble Projects shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of — or inability to use — this site or its content.
            </p>
            <p>
              Our total liability to you for any claim arising from your use of the site shall not exceed the amount you have paid to us in the twelve months preceding the claim. As the service is currently free, this amount is zero.
            </p>
            <p>
              Nothing in these terms excludes or limits our liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded under English law.
            </p>
          </section>

          <section>
            <h2>Third-Party Links</h2>
            <p>
              This site may contain links to third-party websites. These links are provided for your convenience only. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.
            </p>
          </section>

          <section>
            <h2>Changes to These Terms</h2>
            <p>
              We may update these terms from time to time. We will note the date of the most recent revision at the top of this page. Continued use of the site after changes are posted constitutes your acceptance of the revised terms.
            </p>
            <p>
              If we make material changes, we will make reasonable efforts to notify registered users.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have any questions about these terms, please contact us:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:hello@the-gardeners-calendar.co.uk">hello@the-gardeners-calendar.co.uk</a><br />
              <strong>Website:</strong> <a href="https://www.the-gardeners-calendar.co.uk">the-gardeners-calendar.co.uk</a>
            </p>
          </section>

        </article>
      </div>
    </div>
  );
}
